import * as React from 'react'
import { useId } from 'react'
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form'
import { composeRefs } from '@radix-ui/react-compose-refs'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import clsx from 'clsx'
import { isRHFProps } from '@/core/utils/form'
import * as styles from './Textarea.css'

// =============================================================================
// Types
// =============================================================================

type TextareaBaseProps = {
  /** Label text (required) */
  label: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Show error styling (red border/label) */
  error?: boolean
  /** Whether the field is optional (shows "(Optional)" indicator) */
  optional?: boolean
  /** Whether to show the "(Optional)" indicator when optional is true. Defaults to true. */
  showOptional?: boolean
  /** Additional class name for the wrapper */
  className?: string
  /** Number of visible text rows */
  rows?: number
  /** Auto-resize textarea to fit content */
  autoResize?: boolean
  /** Show character counter when maxLength is set */
  showCounter?: boolean
}

// Props for standalone usage (no react-hook-form)
type TextareaStandaloneProps = TextareaBaseProps &
  Omit<React.ComponentPropsWithoutRef<'textarea'>, 'className'>

// Props for react-hook-form usage
type TextareaRHFProps<T extends FieldValues, N extends FieldPath<T>> = TextareaBaseProps & {
  /** Field name for react-hook-form */
  name: N
  /** Control object from useForm */
  control: Control<T>
} & Omit<React.ComponentPropsWithoutRef<'textarea'>, 'className' | 'name' | 'value' | 'onChange' | 'onBlur'>

export type TextareaProps<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>> =
  | TextareaStandaloneProps
  | TextareaRHFProps<T, N>

// =============================================================================
// Component
// =============================================================================

function TextareaInner<T extends FieldValues, N extends FieldPath<T>>(
  props: TextareaProps<T, N>,
  forwardedRef: React.ForwardedRef<HTMLTextAreaElement>
) {
  const {
    label,
    size = 'md',
    error: errorProp,
    optional,
    showOptional = true,
    className,
    placeholder,
    disabled,
    rows = 4,
    autoResize,
    showCounter = true,
    maxLength,
    ...restProps
  } = props

  // Generate unique IDs for accessibility
  const generatedId = useId()
  const textareaId = ('id' in restProps && restProps.id) ? restProps.id : generatedId
  const labelId = `${textareaId}-label`

  // Track if textarea has value (for label floating)
  const [hasValue, setHasValue] = React.useState(() => {
    if ('defaultValue' in restProps && restProps.defaultValue) return true
    if ('value' in restProps && restProps.value) return true
    return false
  })

  // Ref for auto-resize
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // Track mount state to disable transitions on initial render
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    requestAnimationFrame(() => setMounted(true))
  }, [])

  // RHF integration
  let rhfField: {
    value?: string
    onChange?: (value: string) => void
    onBlur?: () => void
    ref?: React.Ref<HTMLTextAreaElement>
    name?: string
  } | null = null
  let fieldError = false

  if (isRHFProps(props)) {
    const { name, control } = props as TextareaRHFProps<T, N>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { field, fieldState } = useController({ name, control })
    rhfField = {
      value: field.value ?? '',
      onChange: field.onChange,
      onBlur: field.onBlur,
      ref: field.ref,
      name: field.name,
    }
    fieldError = !!fieldState.error
  }

  // Determine if we have an error (prop or RHF)
  const hasError = errorProp ?? fieldError

  // Determine if label should be floated
  const hasPlaceholder = !!placeholder
  const hasAnyValue = hasValue || !!rhfField?.value || !!('value' in restProps && restProps.value)
  const shouldFloatLabel = hasPlaceholder || hasAnyValue

  // Track character count (for counter display)
  const [charCount, setCharCount] = React.useState(() => {
    if ('defaultValue' in restProps && restProps.defaultValue) {
      return String(restProps.defaultValue).length
    }
    if ('value' in restProps && restProps.value) {
      return String(restProps.value).length
    }
    return 0
  })

  // Get current value for other purposes
  const currentValue = rhfField?.value ?? ('value' in restProps ? (restProps.value as string) : '') ?? ''

  // Auto-resize handler
  const adjustHeight = React.useCallback(() => {
    if (autoResize && textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto'
      // Set height to scrollHeight
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [autoResize])

  // Adjust height on value change and on mount
  React.useEffect(() => {
    adjustHeight()
  }, [currentValue, adjustHeight])

  // Also adjust on mount for default values
  React.useEffect(() => {
    if (autoResize) {
      // Use setTimeout to ensure the textarea has rendered
      setTimeout(adjustHeight, 0)
    }
  }, [autoResize, adjustHeight])

  // Handle change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setHasValue(!!newValue)
    setCharCount(newValue.length)

    if (rhfField?.onChange) {
      rhfField.onChange(newValue)
    }

    if ('onChange' in restProps && restProps.onChange) {
      restProps.onChange(e)
    }

    // Adjust height for auto-resize (called after state updates)
    if (autoResize && textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  // Handle blur
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (rhfField?.onBlur) {
      rhfField.onBlur()
    }
    if ('onBlur' in restProps && restProps.onBlur) {
      restProps.onBlur(e)
    }
  }

  // Update hasValue and charCount when RHF value changes
  React.useEffect(() => {
    if (rhfField?.value !== undefined) {
      setHasValue(!!rhfField.value)
      setCharCount(rhfField.value.length)
    }
  }, [rhfField?.value])

  // Update charCount when controlled value changes
  React.useEffect(() => {
    if ('value' in restProps && restProps.value !== undefined) {
      setCharCount(String(restProps.value).length)
    }
  }, [restProps])

  // Check initial value on mount
  React.useEffect(() => {
    if (textareaRef.current) {
      setHasValue(!!textareaRef.current.value)
    }
  }, [])

  // Merge refs
  const mergedRef = composeRefs(forwardedRef, textareaRef, rhfField?.ref)

  // Counter state
  const showCharCounter = showCounter && maxLength !== undefined
  const isNearLimit = maxLength !== undefined && charCount >= maxLength * 0.8
  const isAtLimit = maxLength !== undefined && charCount >= maxLength

  // Build textarea props
  const textareaProps: React.ComponentPropsWithoutRef<'textarea'> = {
    ...restProps,
    id: textareaId,
    placeholder,
    disabled,
    rows,
    maxLength,
    className: clsx(
      styles.textarea,
      autoResize && styles.textareaAutoResize
    ),
    onChange: handleChange,
    onBlur: handleBlur,
    name: rhfField?.name ?? ('name' in restProps ? restProps.name : undefined),
    value: rhfField?.value ?? ('value' in restProps ? restProps.value : undefined),
    // Accessibility attributes
    'aria-labelledby': labelId,
    'aria-invalid': hasError || undefined,
    'aria-required': !optional || undefined,
  }

  return (
    <div className={clsx(
      styles.wrapper({ size, error: hasError, disabled }),
      !mounted && styles.noTransition,
      className
    )}>
      <div
        className={clsx(
          styles.textareaContainer,
          hasError && styles.textareaContainerError,
          disabled && styles.textareaContainerDisabled
        )}
      >
        {/* Content area with textarea and floating label */}
        <div className={styles.content}>
          {/* Textarea */}
          <textarea ref={mergedRef} {...textareaProps} />

          {/* Floating label */}
          <label
            id={labelId}
            htmlFor={textareaId}
            className={clsx(
              styles.label,
              shouldFloatLabel && styles.labelFloated,
              hasError && styles.labelError,
              disabled && styles.labelDisabled
            )}
          >
            {label}
            {optional && <VisuallyHidden.Root> (optional)</VisuallyHidden.Root>}
          </label>

          {/* Optional indicator */}
          {optional && showOptional && (
            <span
              aria-hidden="true"
              className={clsx(
                styles.optional,
                shouldFloatLabel && styles.optionalFloated
              )}
            >
              (Optional)
            </span>
          )}
        </div>
      </div>

      {/* Character counter */}
      {showCharCounter && (
        <div className={styles.footer}>
          <span
            className={clsx(
              styles.counter,
              isNearLimit && !isAtLimit && styles.counterWarning,
              isAtLimit && styles.counterError
            )}
          >
            {charCount}/{maxLength}
          </span>
        </div>
      )}
    </div>
  )
}

// ForwardRef wrapper with generic support
export const Textarea = React.forwardRef(TextareaInner) as <
  T extends FieldValues = FieldValues,
  N extends FieldPath<T> = FieldPath<T>
>(
  props: TextareaProps<T, N> & { ref?: React.ForwardedRef<HTMLTextAreaElement> }
) => React.ReactElement

// Display name for debugging
(Textarea as React.FC).displayName = 'Textarea'
